import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useParams, Link } from 'react-router-dom'
import { useState, useCallback, useEffect, useRef } from 'react'
import {
  ArrowLeft,
  BarChart3,
  Download,
  Users,
  TrendingUp,
  Package,
  Target,
  CheckCircle,
  Shield,
  Eye,
  Star,
  X,
  CalendarDays,
  Share2,
  Building2,
  Lightbulb,
  AlertTriangle,
  ThumbsUp,
  ThumbsDown,
  Zap,
  Activity,
  Compass,
  Globe,
  Rocket,
  LineChart,
  DollarSign,
  MessageSquare,
  Award,
  Lock,
  TrendingDown,
  FileText,
  Clock,
  CheckSquare,
  RefreshCw,
  ExternalLink,
  Link as LinkIcon,
  ChevronLeft,
  ChevronRight,
  MessageSquarePlus,
} from 'lucide-react'
import ReactMarkdown from 'react-markdown'
import { FeedbackModal } from '@/components/ui/FeedbackModal'

import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Progress } from '@/components/ui/progress'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Textarea } from '@/components/ui/textarea'
import { Label } from '@/components/ui/label'
import { analysisApi, startupStatusApi, startupsApi } from '@/services/api'
import { getScoreColor, getScoreLabel, getInvestmentRecommendationColor } from '@/utils'
import { useToast } from '@/hooks/use-toast'
import { BeaconProvider, BeaconChat, BeaconButton, BeaconSectionIcon, BeaconSubsectionIcon } from '@/components/beacon'
import { InvestmentMemoSection } from '@/components/investment-memo-section'
import { DocumentPreviewModal } from '@/components/document-preview-modal'
import { StartupAnalysisBanner } from '@/components/startup-analysis-banner'

// Custom event name for document preview (used instead of React Context for HMR compatibility)
const DOCUMENT_PREVIEW_EVENT = 'uniscout:document-preview'

// Custom styles mapping to user request
const displayFont = { fontFamily: '"Inter", sans-serif' }
const headingFont = { fontFamily: '"Outfit", sans-serif' }

// SourceCitationLink - Renders source citations as superscript icons with tooltips
// Handles both file sources [Source: filename, Page: X] and web sources [Web: domain]
function SourceCitationLink({ href, children }: { href?: string, children: React.ReactNode }) {
  const text = String(children || '')

  // Check if this is a file source citation (text contains "Source:")
  const isFileSourceText = text.startsWith('Source:') || text.includes('Source:')
  // Check if href is a proper source: link
  const hasSourceLink = href?.startsWith('source:')

  // Check if this is a web source citation (text contains "Web:")
  const isWebSourceText = text.startsWith('Web:') || text.includes('Web:')
  // Check if href is a proper http link
  const hasWebLink = href?.startsWith('http')

  // Parse GCS path and page from source: link
  // Format: source:gs://bucket/path/file.pdf#page=X or source:missing
  const parseSourceLink = (sourceHref: string): { gcsPath: string; page: number | null } | null => {
    if (!sourceHref || sourceHref === 'source:missing') return null
    const rawPath = sourceHref.replace(/^source:/, '')
    if (!rawPath.startsWith('gs://')) return null
    const [path, fragment] = rawPath.split('#')
    let page: number | null = null
    if (fragment) {
      const pageMatch = fragment.match(/page=(\d+)/)
      if (pageMatch) page = parseInt(pageMatch[1], 10)
    }
    return { gcsPath: decodeURIComponent(path), page }
  }

  // Handle web sources
  if (isWebSourceText) {
    if (hasWebLink) {
      // Web source with valid URL - render as superscript external link icon
      return (
        <a
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center align-super group relative ml-0.5"
          title="Web Source"
        >
          <span className="inline-flex items-center justify-center w-4 h-4 rounded bg-blue-100 dark:bg-blue-900/30 hover:bg-blue-200 dark:hover:bg-blue-900/50 transition-colors">
            <ExternalLink className="w-2.5 h-2.5 text-blue-600 dark:text-blue-400" />
          </span>
          <span className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-1 px-2 py-1 text-xs font-medium text-white bg-gray-900 dark:bg-gray-700 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none z-50">
            Web Source
          </span>
        </a>
      )
    } else {
      // Web source WITHOUT valid URL - just show "Web Scouting" as plain text
      return (
        <span className="inline-flex items-center align-super group relative ml-0.5">
          <span className="inline-flex items-center justify-center w-4 h-4 rounded bg-blue-100 dark:bg-blue-900/30">
            <Globe className="w-2.5 h-2.5 text-blue-600 dark:text-blue-400" />
          </span>
          <span className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-1 px-2 py-1 text-xs font-medium text-white bg-gray-900 dark:bg-gray-700 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none z-50">
            Web Scouting
          </span>
        </span>
      )
    }
  }

  // Handle file sources
  if (isFileSourceText) {
    // Extract filename from text like "Source: filename.pdf, Page: 3" or "Source: filename.pdf, Pages: 1-5"
    const filenameMatch = text.match(/Source:\s*([^,\]]+)/)
    const filename = filenameMatch ? filenameMatch[1].trim() : 'File'
    const pageMatch = text.match(/Page[s]?:\s*([\d\-,\s]+)/)
    const page = pageMatch ? pageMatch[1].trim() : null

    const tooltipText = page ? `${filename} (Page ${page})` : filename

    if (hasSourceLink) {
      const parsed = parseSourceLink(href!)
      const isPreviewable = !!parsed
      const tooltipAction = isPreviewable ? `📄 ${tooltipText} — Click to preview` : tooltipText

      // File source with valid source: link - render as clickable superscript icon
      return (
        <span
          className="inline-flex items-center align-super group relative ml-0.5 cursor-pointer"
          title={tooltipAction}
          onClick={isPreviewable ? () => {
            const pageNum = parsed!.page ?? (page ? parseInt(page.split('-')[0], 10) : null)
            window.dispatchEvent(new CustomEvent(DOCUMENT_PREVIEW_EVENT, {
              detail: { gcsPath: parsed!.gcsPath, filename, page: pageNum }
            }))
          } : undefined}
        >
          <span className="inline-flex items-center justify-center w-4 h-4 rounded bg-amber-100 dark:bg-amber-900/30 hover:bg-amber-200 dark:hover:bg-amber-900/50 transition-colors">
            <FileText className="w-2.5 h-2.5 text-amber-600 dark:text-amber-400" />
          </span>
          <span className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-1 px-2 py-1 text-xs font-medium text-white bg-gray-900 dark:bg-gray-700 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none z-50">
            {tooltipAction}
          </span>
        </span>
      )
    } else {
      // File source WITHOUT valid source: link - just show filename as plain text indicator
      return (
        <span className="inline-flex items-center align-super group relative ml-0.5">
          <span className="inline-flex items-center justify-center w-4 h-4 rounded bg-amber-100 dark:bg-amber-900/30">
            <FileText className="w-2.5 h-2.5 text-amber-600 dark:text-amber-400" />
          </span>
          <span className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-1 px-2 py-1 text-xs font-medium text-white bg-gray-900 dark:bg-gray-700 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none z-50">
            {tooltipText}
          </span>
        </span>
      )
    }
  }

  // Not a citation - check if it's a regular link
  if (href) {
    return (
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 underline"
      >
        {children}
      </a>
    )
  }

  return <>{children}</>
}

// Custom components for ReactMarkdown to handle citation links
const markdownComponents = {
  a: ({ href, children }: { href?: string, children?: React.ReactNode }) => (
    <SourceCitationLink href={href}>{children}</SourceCitationLink>
  ),
}

// Custom URL transform to allow source: protocol through ReactMarkdown's sanitization.
// By default, react-markdown v9 strips non-standard protocols like source:
function allowSourceProtocol(url: string): string {
  if (url.startsWith('source:')) return url
  // Fall back to default sanitization for other URLs
  const decoded = url.startsWith('data:') ? url : decodeURIComponent(url)
  if (decoded.startsWith('javascript:') || decoded.startsWith('vbscript:')) return ''
  return url
}

// Pre-process text to fix citation markdown so ReactMarkdown can parse them.
// Handles two issues:
// 1. URLs with spaces (GCS paths) that break markdown link parsing
// 2. Bare [Source: ...] or [Web: ...] citations without a (url) part
function preprocessCitationText(text: string): string {
  // Step 1: Encode spaces in existing markdown link URLs
  let result = text.replace(
    /\[([^\]]*)\]\(([^)]+)\)/g,
    (_match, linkText, url) => {
      const encodedUrl = url.replace(/ /g, '%20')
      return `[${linkText}](${encodedUrl})`
    }
  )

  // Step 2: Convert bare [Source: ...] citations (not followed by a URL) into markdown links
  // so ReactMarkdown creates <a> elements that SourceCitationLink can render as icons.
  // Matches [Source: ...] NOT immediately followed by (
  result = result.replace(
    /\[(Source:[^\]]+)\](?!\()/g,
    (_match, citationText) => `[${citationText}](source:missing)`
  )

  // Step 3: Same for bare [Web: ...] citations
  result = result.replace(
    /\[(Web:[^\]]+)\](?!\()/g,
    (_match, citationText) => `[${citationText}](source:missing)`
  )

  return result
}

// Helper function to render text with citation support
// Use this anywhere text needs to be rendered with proper citation formatting
function TextWithCitations({ children, className = "" }: { children: string, className?: string }) {
  return (
    <span className={`prose prose-sm max-w-none dark:prose-invert ${className}`}>
      <ReactMarkdown components={markdownComponents} urlTransform={allowSourceProtocol}>{preprocessCitationText(children)}</ReactMarkdown>
    </span>
  )
}

// Helper function to extract subsection headings from analysis data
function extractSubsections(data: any): string[] {
  if (!data || typeof data !== 'object') return []
  const subsections: string[] = []
  Object.keys(data).forEach(key => {
    const formatted = key.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')
    if (!['score', 'agent_name', 'analysis', 'response', 'detailed_analysis'].includes(key)) {
      subsections.push(formatted)
    }
  })
  return subsections
}

// Format USD values for display
function formatUSD(value: number | undefined | null): string {
  if (value === undefined || value === null) return 'N/A'
  if (value >= 1_000_000_000) return `$${(value / 1_000_000_000).toFixed(1)}B`
  if (value >= 1_000_000) return `$${(value / 1_000_000).toFixed(0)}M`
  if (value >= 1_000) return `$${(value / 1_000).toFixed(0)}K`
  return `$${value}`
}

function formatNumber(value: number | undefined | null): string {
  if (value === undefined || value === null) return 'N/A'
  if (value >= 1_000_000) return `${(value / 1_000_000).toFixed(1)}M`
  if (value >= 1_000) return `${(value / 1_000).toFixed(1)}K`
  return `${value}`
}

function getScoreDotColor(score: number): string {
  if (score >= 8) return 'bg-green-500'
  if (score >= 6) return 'bg-blue-500'
  if (score >= 4) return 'bg-yellow-500'
  return 'bg-red-500'
}

// Score pill badge component
function ScorePill({ label, value }: { label: string; value: number | undefined }) {
  if (value === undefined || value === null) return null
  return (
    <div className="flex items-center bg-gray-50 dark:bg-gray-800 rounded-full px-4 py-1.5 border border-gray-200 dark:border-gray-700">
      <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mr-2">{label}</span>
      <span className="text-base font-bold text-gray-900 dark:text-white mr-2">{value.toFixed(1)}</span>
      <div className={`w-2 h-2 rounded-full ${getScoreDotColor(value)}`} />
    </div>
  )
}

// Executive briefing section
function ExecutiveBriefing({ summary }: { summary: string }) {
  if (!summary) return null
  // Split summary into sentences for bullet points
  const bullets = summary.split(/(?<=[.!?])\s+/).filter(s => s.trim().length > 20).slice(0, 4)
  return (
    <div className="mb-10">
      <div className="flex items-center gap-2 mb-4">
        <Lightbulb className="w-5 h-5 text-blue-500" />
        <h3 className="text-lg font-bold text-gray-900 dark:text-white">Executive Briefing</h3>
      </div>
      <div className="bg-gray-50 dark:bg-gray-800/50 border border-gray-200 dark:border-gray-700 rounded-xl p-5">
        <div className="flex gap-4">
          <div className="mt-1 hidden sm:block">
            <BarChart3 className="w-5 h-5 text-gray-400" />
          </div>
          <div>
            <span className="text-xs font-bold text-gray-500 uppercase tracking-wide">EXECUTIVE_SUMMARY</span>
            <div className="mt-2 space-y-2">
              {bullets.map((bullet, i) => (
                <div key={i} className="flex gap-3 text-sm text-gray-600 dark:text-gray-300">
                  <div className="w-2 h-2 rounded-full bg-blue-400 mt-1.5 flex-shrink-0" />
                  <TextWithCitations>{bullet}</TextWithCitations>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

// Colored list card (Strengths, Weaknesses, Red Flags, Opportunities, Challenges, etc.)
function ColoredListCard({ title, items, variant }: {
  title: string; items: string[]; variant: 'green' | 'amber' | 'red' | 'blue' | 'pink'
}) {
  if (!items || items.length === 0) return null
  const styles = {
    green: { bg: 'bg-green-50 dark:bg-green-900/10', border: 'border-green-100 dark:border-green-800', title: 'text-green-800 dark:text-green-400', dot: 'bg-green-500' },
    amber: { bg: 'bg-amber-50 dark:bg-amber-900/10', border: 'border-amber-100 dark:border-amber-800', title: 'text-amber-800 dark:text-amber-500', dot: 'bg-amber-500' },
    red: { bg: 'bg-red-50 dark:bg-red-900/10', border: 'border-red-100 dark:border-red-800', title: 'text-red-800 dark:text-red-500', dot: 'bg-red-500' },
    blue: { bg: 'bg-blue-50 dark:bg-blue-900/10', border: 'border-blue-100 dark:border-blue-800', title: 'text-blue-800 dark:text-blue-400', dot: 'bg-blue-500' },
    pink: { bg: 'bg-pink-50 dark:bg-pink-900/10', border: 'border-pink-100 dark:border-pink-800', title: 'text-pink-800 dark:text-pink-400', dot: 'bg-pink-500' },
  }
  const s = styles[variant]
  return (
    <div className={`${s.bg} border ${s.border} rounded-2xl p-6`}>
      <h4 className={`text-xs font-bold ${s.title} uppercase tracking-wider mb-3`}>{title}</h4>
      <ul className="space-y-2">
        {items.map((item, i) => (
          <li key={i} className="flex items-start text-sm text-gray-700 dark:text-gray-300">
            <span className={`w-1.5 h-1.5 rounded-full ${s.dot} mt-1.5 mr-2.5 flex-shrink-0`} />
            <TextWithCitations>{item}</TextWithCitations>
          </li>
        ))}
      </ul>
    </div>
  )
}

// Metric card component
function MetricCard({ label, value, subtitle, subtitleColor, tooltip }: {
  label: string; value: string; subtitle?: string; subtitleColor?: string; tooltip?: string
}) {
  return (
    <div className="relative group/tooltip bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-700 rounded-xl p-4 shadow-sm hover:shadow-md transition cursor-default">
      <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">{label}</div>
      <div className="text-xl font-bold text-gray-900 dark:text-white">{value}</div>
      {subtitle && <div className={`text-xs font-medium mt-1 ${subtitleColor || 'text-gray-400'}`}>{subtitle}</div>}
      {tooltip && (
        <div className="absolute z-50 bottom-full left-1/2 -translate-x-1/2 mb-2 w-64 p-3 bg-gray-900 dark:bg-gray-700 text-white text-xs leading-relaxed rounded-lg shadow-lg opacity-0 invisible group-hover/tooltip:opacity-100 group-hover/tooltip:visible transition-all duration-200 pointer-events-none">
          {tooltip}
          <div className="absolute top-full left-1/2 -translate-x-1/2 w-0 h-0 border-x-[6px] border-x-transparent border-t-[6px] border-t-gray-900 dark:border-t-gray-700" />
        </div>
      )}
    </div>
  )
}

// Section header component
function SectionHeader({ icon: Icon, title, className = '' }: { icon: any; title: string; className?: string }) {
  return (
    <div className={`flex items-center gap-2 mb-4 ${className}`}>
      <Icon className="w-5 h-5 text-blue-500" />
      <h3 className="text-lg font-bold text-gray-900 dark:text-white">{title}</h3>
    </div>
  )
}

// Team member card
function TeamMemberCard({ member }: { member: any }) {
  const initials = member.name?.split(' ').map((n: string) => n[0]).join('').slice(0, 2).toUpperCase() || '?'
  return (
    <div className="w-full h-full flex flex-col bg-gray-50 dark:bg-gray-800/50 border border-gray-200 dark:border-gray-700 rounded-2xl p-6 hover:shadow-md transition duration-300">
      <div className="flex items-start gap-4 mb-4">
        <div className="w-12 h-12 rounded-xl bg-gray-300 dark:bg-gray-600 flex-shrink-0 overflow-hidden flex items-center justify-center border border-gray-200 dark:border-gray-500">
          <span className="text-xs font-bold text-gray-600 dark:text-gray-300">{initials}</span>
        </div>
        <div>
          <h4 className="text-lg font-bold text-gray-900 dark:text-white">{member.name}</h4>
          <p className="text-xs text-gray-500 dark:text-gray-400 font-medium">{member.title}</p>
        </div>
      </div>
      {member.background && (
        <div className="text-sm text-gray-600 dark:text-gray-300 mb-4 leading-relaxed max-h-32 overflow-y-auto pr-2">
          <TextWithCitations>{member.background}</TextWithCitations>
        </div>
      )}
      <div className="mt-auto">
        <div className="flex flex-wrap gap-2">
          {member.is_founder && (
            <span className="px-2.5 py-1 bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 text-[10px] font-bold rounded uppercase">Founder</span>
          )}
          {member.expertise_areas?.slice(0, 3).map((area: string, i: number) => (
            <span key={i} className="px-2.5 py-1 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 text-[10px] font-bold rounded uppercase">{area}</span>
          ))}
          {member.years_of_experience && (
            <span className="px-2.5 py-1 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 text-[10px] font-bold rounded uppercase">{member.years_of_experience}+ yrs</span>
          )}
        </div>
        {member.notable_achievements?.length > 0 && (
          <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-700">
            <p className="text-[10px] font-bold text-gray-400 uppercase mb-1">Key Achievements</p>
            <ul className="space-y-1">
              {member.notable_achievements.slice(0, 2).map((a: string, i: number) => (
                <li key={i} className="text-xs text-gray-500 dark:text-gray-400 flex items-start gap-1.5">
                  <Award className="w-3 h-3 mt-0.5 flex-shrink-0 text-yellow-500" />
                  {a}
                </li>
              ))}
            </ul>
          </div>
        )}
        {member.linkedin_url && (
          <a href={member.linkedin_url} target="_blank" rel="noopener noreferrer" className="mt-2 inline-flex items-center gap-1 text-xs text-blue-600 hover:underline">
            <ExternalLink className="w-3 h-3" /> LinkedIn
          </a>
        )}
      </div>
    </div>
  )
}

// Competitor card for the competition tab
function CompetitorCardComponent({ competitor }: { competitor: any }) {
  const [logoError, setLogoError] = useState(false)
  const hasLogo = competitor.public_logo_url && !logoError
  const threatColors: Record<string, string> = {
    Critical: 'text-red-600 dark:text-red-400', High: 'text-red-500 dark:text-red-400',
    Medium: 'text-yellow-600 dark:text-yellow-400', Low: 'text-green-600 dark:text-green-400',
  }
  return (
    <div className="bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-700 rounded-2xl p-5 hover:shadow-md transition">
      <div className="flex items-start gap-3 mb-3">
        <div className="w-10 h-10 rounded-full bg-gray-100 dark:bg-gray-700 flex items-center justify-center flex-shrink-0 overflow-hidden border border-gray-200 dark:border-gray-600">
          {hasLogo ? (
            <img src={competitor.public_logo_url} alt={competitor.name} className="w-full h-full object-contain" onError={() => setLogoError(true)} />
          ) : (
            <Building2 className="w-5 h-5 text-gray-400" />
          )}
        </div>
        <div className="flex-1 min-w-0">
          {competitor.public_url ? (
            <a href={`https://${competitor.public_url}`} target="_blank" rel="noopener noreferrer" className="font-bold text-gray-900 dark:text-white hover:text-blue-600 truncate block">{competitor.name}</a>
          ) : (
            <h4 className="font-bold text-gray-900 dark:text-white truncate">{competitor.name}</h4>
          )}
          <div className="flex items-center gap-2 mt-0.5">
            {competitor.category && <span className="text-[10px] font-bold text-blue-600 dark:text-blue-400 uppercase">{competitor.category}</span>}
            {competitor.founded_year && <span className="text-[10px] text-gray-400">Est. {competitor.founded_year}</span>}
          </div>
        </div>
        {competitor.threat_level && (
          <div className="text-right flex-shrink-0">
            <span className="text-[9px] text-gray-400 uppercase block">Threat</span>
            <span className={`text-xs font-bold ${threatColors[competitor.threat_level] || 'text-gray-500'}`}>{competitor.threat_level}</span>
          </div>
        )}
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 text-xs mb-3">
        {competitor.funding_raised != null && <div><span className="text-gray-400">Funding</span><br /><span className="font-bold text-gray-900 dark:text-white">${(competitor.funding_raised / 1_000_000).toFixed(0)}M</span></div>}
        {competitor.valuation != null && <div><span className="text-gray-400">Valuation</span><br /><span className="font-bold text-gray-900 dark:text-white">${(competitor.valuation / 1_000_000).toFixed(0)}M</span></div>}
        {competitor.employee_count != null && <div><span className="text-gray-400">Employees</span><br /><span className="font-bold text-gray-900 dark:text-white">{competitor.employee_count.toLocaleString()}</span></div>}
        {competitor.market_share != null && <div><span className="text-gray-400">Market Share</span><br /><span className="font-bold text-gray-900 dark:text-white">{competitor.market_share}%</span></div>}
        {competitor.revenue_estimate != null && <div><span className="text-gray-400">Revenue</span><br /><span className="font-bold text-gray-900 dark:text-white">${(competitor.revenue_estimate / 1_000_000).toFixed(0)}M</span></div>}
        {competitor.customer_count != null && <div><span className="text-gray-400">Customers</span><br /><span className="font-bold text-gray-900 dark:text-white">{competitor.customer_count.toLocaleString()}</span></div>}
      </div>
      {competitor.strengths?.length > 0 && (
        <div className="mb-2">
          <span className="text-[10px] font-bold text-green-700 dark:text-green-400 uppercase">Strengths</span>
          <ul className="mt-1 space-y-1">{competitor.strengths.slice(0, 2).map((s: string, i: number) => (
            <li key={i} className="text-xs text-gray-600 dark:text-gray-400 flex items-start gap-1.5"><span className="w-1 h-1 rounded-full bg-green-500 mt-1.5 flex-shrink-0" />{s}</li>
          ))}</ul>
        </div>
      )}
      {competitor.weaknesses?.length > 0 && (
        <div className="mb-2">
          <span className="text-[10px] font-bold text-amber-700 dark:text-amber-400 uppercase">Weaknesses</span>
          <ul className="mt-1 space-y-1">{competitor.weaknesses.slice(0, 2).map((w: string, i: number) => (
            <li key={i} className="text-xs text-gray-600 dark:text-gray-400 flex items-start gap-1.5"><span className="w-1 h-1 rounded-full bg-amber-500 mt-1.5 flex-shrink-0" />{w}</li>
          ))}</ul>
        </div>
      )}
      {competitor.differentiation && (
        <div className="mt-2 pt-2 border-t border-gray-100 dark:border-gray-700">
          <span className="text-[10px] font-bold text-purple-700 dark:text-purple-400 uppercase">Differentiation</span>
          <p className="text-xs text-gray-600 dark:text-gray-400 mt-1 line-clamp-2">{competitor.differentiation}</p>
        </div>
      )}
    </div>
  )
}

// Web Sources section
function WebSourcesSection({ chunks }: { chunks: any[] }) {
  if (!chunks || chunks.length === 0) return null
  const validChunks = chunks.filter((item: any) => item.title || item.url)
  if (validChunks.length === 0) return null
  return (
    <div className="mt-8">
      <SectionHeader icon={LinkIcon} title="Web Sources" />
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-2">
        {validChunks.map((item: any, i: number) => (
          <a key={i} href={item.url} target="_blank" rel="noopener noreferrer"
            className="text-xs text-blue-600 dark:text-blue-400 hover:underline truncate block py-1"
            title={item.title || item.url}
          >{item.title || 'Source'}</a>
        ))}
      </div>
    </div>
  )
}

// Questions section
function QuestionsSection({ questions }: { questions: string[] }) {
  if (!questions || questions.length === 0) return null
  return (
    <div className="mt-8">
      <SectionHeader icon={MessageSquare} title="Key Questions" />
      <div className="bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-700 rounded-2xl p-6">
        <ul className="space-y-3">
          {questions.map((q, i) => (
            <li key={i} className="flex items-start gap-3 text-sm text-gray-600 dark:text-gray-300">
              <span className="flex-shrink-0 w-6 h-6 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center text-xs font-bold text-blue-600">{i + 1}</span>
              <TextWithCitations>{q}</TextWithCitations>
            </li>
          ))}
        </ul>
      </div>
    </div>
  )
}

// Recommendations section
function RecommendationsSection({ recommendations }: { recommendations: string[] }) {
  if (!recommendations || recommendations.length === 0) return null
  return (
    <div className="mt-8">
      <SectionHeader icon={Lightbulb} title="Recommendations" />
      <div className="bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-700 rounded-2xl p-6">
        <ul className="space-y-3">
          {recommendations.map((r, i) => (
            <li key={i} className="flex items-start gap-3 text-sm text-gray-600 dark:text-gray-300">
              <span className="flex-shrink-0 w-6 h-6 rounded-full bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center text-xs font-bold text-purple-600">{i + 1}</span>
              <TextWithCitations>{r}</TextWithCitations>
            </li>
          ))}
        </ul>
      </div>
    </div>
  )
}

// Confidence level indicator component
function ConfidenceIndicator({ confidence }: { confidence: number | undefined | null }) {
  if (confidence === undefined || confidence === null) return null
  const pct = Math.round(confidence * 100)
  const color = pct >= 70 ? 'bg-green-500' : pct >= 40 ? 'bg-amber-500' : 'bg-red-500'
  const textColor = pct >= 70 ? 'text-green-600 dark:text-green-400' : pct >= 40 ? 'text-amber-600 dark:text-amber-400' : 'text-red-600 dark:text-red-400'
  return (
    <div className="mt-10 pt-6 border-t border-gray-100 dark:border-gray-800">
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs font-bold text-gray-400 uppercase tracking-wider">Analysis Confidence</span>
        <span className={`text-sm font-bold ${textColor}`}>{pct}%</span>
      </div>
      <div className="h-1.5 bg-gray-100 dark:bg-gray-800 rounded-full overflow-hidden">
        <div className={`h-full ${color} rounded-full transition-all duration-500`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  )
}

// Text section with icon header and bullet list
function TextSection({ icon: Icon, title, text, iconColor = 'text-blue-500' }: { icon: any; title: string; text: string; iconColor?: string }) {
  if (!text) return null
  return (
    <div className="bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-700 rounded-2xl p-6 shadow-sm">
      <div className="flex items-center gap-2 mb-4">
        <Icon className={`w-5 h-5 ${iconColor}`} />
        <h3 className="text-xs font-bold text-gray-900 dark:text-white uppercase tracking-wider">{title}</h3>
      </div>
      <div className="space-y-3">
        <div className="text-sm text-gray-600 dark:text-gray-300 leading-relaxed">
          <TextWithCitations>{text}</TextWithCitations>
        </div>
      </div>
    </div>
  )
}

// Component to render analysis data with structured layout and scores
function AnalysisRenderer({ data, title, icon: Icon, analysisType }: {
  data: any,
  title: string,
  icon: any,
  analysisType: 'team' | 'market' | 'product' | 'competition' | 'synthesis'
}) {
  const [isFeedbackOpen, setIsFeedbackOpen] = useState(false);

  if (!data) {
    return (
      <Card>
        <CardHeader><CardTitle className="flex items-center gap-2"><Icon className="w-5 h-5" />{title}</CardTitle></CardHeader>
        <CardContent><p className="text-muted-foreground">No analysis data available</p></CardContent>
      </Card>
    )
  }

  // Get scores based on analysis type
  const getScorePills = () => {
    switch (analysisType) {
      case 'team':
        return [
          { label: 'Founders', value: data.founder_market_fit_score },
          { label: 'Completeness', value: data.team_completeness_score },
          { label: 'Experience', value: data.experience_score },
          { label: 'Leadership', value: data.leadership_score },
        ]
      case 'market':
        return [
          { label: 'Size', value: data.market_size_score },
          { label: 'Growth', value: data.market_growth_score },
          { label: 'Timing', value: data.market_timing_score },
          { label: 'Access', value: data.market_accessibility_score },
        ]
      case 'product':
        return [
          { label: 'PMF', value: data.product_market_fit_score },
          { label: 'Value Prop', value: data.value_proposition_score },
          { label: 'Traction', value: data.traction_score },
          { label: 'Scale', value: data.scalability_score },
        ]
      case 'competition':
        return [
          { label: 'Landscape', value: data.competitive_landscape_score },
          { label: 'Diff.', value: data.differentiation_score },
          { label: 'Moat', value: data.moat_strength_score },
          { label: 'Position', value: data.market_positioning_score },
        ]
      default: return []
    }
  }

  const scorePills = getScorePills().filter(s => s.value !== undefined && s.value !== null)

  return (
    <div className="space-y-6">
      {/* Tab Header with Score Pills */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="p-2.5 bg-logo-gradient rounded-2xl shadow-lg">
            <Icon className="w-6 h-6 md:w-8 md:h-8 text-white" />
          </div>
          <div>
            <div className="flex items-center gap-3">
              <h2 className="text-2xl md:text-3xl font-black text-gray-900 dark:text-white" style={headingFont}>{title}</h2>
              <button
                onClick={() => setIsFeedbackOpen(true)}
                className="flex items-center gap-1.5 px-2.5 py-1 text-[11px] font-bold text-indigo-600 bg-indigo-50 hover:bg-indigo-100 dark:bg-indigo-900/30 dark:text-indigo-300 dark:hover:bg-indigo-900/50 rounded-full transition-colors border border-indigo-100 dark:border-indigo-800"
                title="Provide feedback on this section"
              >
                <MessageSquarePlus className="w-3.5 h-3.5" />
                Feedback
              </button>
            </div>
            {data.overall_score !== undefined && (
              <p className="text-xs font-bold text-gray-400 mt-0.5">
                Overall Score: <span className={getScoreColor(data.overall_score)}>{data.overall_score.toFixed(1)}</span>/10
              </p>
            )}
          </div>
        </div>
        {scorePills.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {scorePills.map((pill, i) => <ScorePill key={i} label={pill.label} value={pill.value} />)}
          </div>
        )}
      </div>

      {/* Tab-specific content */}
      <div className="bg-white dark:bg-gray-900 rounded-3xl border border-gray-100 dark:border-gray-800 p-4 md:p-8 shadow-sm">
        {analysisType === 'team' && <TeamTabContent data={data} />}
        {analysisType === 'market' && <MarketTabContent data={data} />}
        {analysisType === 'product' && <ProductTabContent data={data} />}
        {analysisType === 'competition' && <CompetitionTabContent data={data} />}
        {analysisType === 'synthesis' && <SynthesisTabContent data={data} />}
      </div>
      <FeedbackModal
        open={isFeedbackOpen}
        onOpenChange={setIsFeedbackOpen}
        page="Analysis Page"
        section={title}
      />
    </div>
  )
}

// ─── TEAM TAB ───────────────────────────────────────────────────────────────────
function TeamTabContent({ data }: { data: any }) {
  const teamScrollRef = useRef<HTMLDivElement>(null)
  const scrollTeam = (dir: 'left' | 'right') => {
    if (!teamScrollRef.current) return
    teamScrollRef.current.scrollBy({ left: dir === 'left' ? -340 : 340, behavior: 'smooth' })
  }

  if (!data) return null
  return (
    <>
      <ExecutiveBriefing summary={data.executive_summary} />

      {/* Founding Team Members - Horizontal Scroll */}
      {data.team_members?.length > 0 && (
        <div className="mb-10">
          <div className="flex items-center justify-between mb-4">
            <SectionHeader icon={Users} title="Founding Team" />
            <div className="flex gap-2">
              <button
                onClick={() => scrollTeam('left')}
                className="p-1.5 rounded-full border border-gray-200 dark:border-gray-700 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
              >
                <ChevronLeft className="w-4 h-4 text-gray-500" />
              </button>
              <button
                onClick={() => scrollTeam('right')}
                className="p-1.5 rounded-full border border-gray-200 dark:border-gray-700 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
              >
                <ChevronRight className="w-4 h-4 text-gray-500" />
              </button>
            </div>
          </div>
          <div
            ref={teamScrollRef}
            className="flex gap-4 overflow-x-auto scrollbar-hide pb-2 -mx-2 px-2 snap-x snap-mandatory"
          >
            {data.team_members.map((member: any, i: number) => (
              <div key={i} className="flex items-stretch min-w-[320px] max-w-[360px] flex-shrink-0 snap-start">
                <TeamMemberCard member={member} />
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Detailed Analysis: Strengths / Weaknesses / Red Flags */}
      <div className="mb-10">
        <SectionHeader icon={Eye} title="Detailed Analysis" />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <ColoredListCard title="Strengths" items={data.strengths} variant="green" />
          <ColoredListCard title="Weaknesses" items={data.weaknesses} variant="amber" />
          <ColoredListCard title="Red Flags" items={data.red_flags} variant="red" />
        </div>
      </div>

      {/* Experience & Leadership sections */}
      <div className="grid grid-cols-1 gap-4 mb-10">
        <TextSection icon={Award} title="Experience Assessment" text={data.experience_assessment} />
        <TextSection icon={Star} title="Leadership & Culture" text={data.leadership_evaluation} />
      </div>

      {/* Team Composition & Founder Analysis */}
      <div className="grid grid-cols-1 gap-4 mb-10">
        <TextSection icon={Users} title="Team Composition" text={data.team_composition} />
        <TextSection icon={Compass} title="Founder Analysis" text={data.founder_analysis} />
      </div>

      <QuestionsSection questions={data.questions} />
      <RecommendationsSection recommendations={data.recommendations} />
      <ConfidenceIndicator confidence={data.confidence_level} />
    </>
  )
}

// ─── MARKET TAB ─────────────────────────────────────────────────────────────────
function MarketTabContent({ data }: { data: any }) {
  const sizing = data.market_sizing || {}
  return (
    <>
      <ExecutiveBriefing summary={data.executive_summary} />

      {/* Market Sizing Metrics */}
      {Object.keys(sizing).length > 0 && (
        <div className="mb-10">
          <SectionHeader icon={DollarSign} title="Market Sizing" />
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
            {sizing.tam_usd !== undefined && <MetricCard label="TAM" value={formatUSD(sizing.tam_usd)} subtitle="Total Addressable" tooltip={sizing.tam_methodology} />}
            {sizing.sam_usd !== undefined && <MetricCard label="SAM" value={formatUSD(sizing.sam_usd)} subtitle="Serviceable" tooltip={sizing.sam_methodology} />}
            {sizing.som_usd !== undefined && <MetricCard label="SOM" value={formatUSD(sizing.som_usd)} subtitle="Obtainable" tooltip={sizing.som_methodology} />}
            {sizing.market_growth_rate !== undefined && <MetricCard label="Growth Rate" value={`${sizing.market_growth_rate}%`} subtitle="Annual" subtitleColor="text-green-500" />}
            {sizing.market_maturity && <MetricCard label="Maturity" value={sizing.market_maturity} />}
            {sizing.confidence_level !== undefined && <MetricCard label="Confidence" value={`${(sizing.confidence_level * 100).toFixed(0)}%`} />}
          </div>
        </div>
      )}

      {/* Opportunities / Challenges / Trends */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-10">
        <ColoredListCard title="Opportunities" items={data.opportunities} variant="green" />
        <ColoredListCard title="Challenges" items={data.challenges} variant="amber" />
        <ColoredListCard title="Supporting Trends" items={data.trends_supporting} variant="blue" />
      </div>

      {/* Opposing Trends */}
      {data.trends_opposing?.length > 0 && (
        <div className="mb-10">
          <ColoredListCard title="Opposing Trends" items={data.trends_opposing} variant="red" />
        </div>
      )}

      {/* Market Context */}
      <div className="grid grid-cols-1 gap-4 mb-10">
        <TextSection icon={Globe} title="Market Definition" text={data.market_definition} />
        <TextSection icon={Clock} title="Market Timing" text={data.market_timing} />
      </div>

      <div className="grid grid-cols-1 gap-4 mb-10">
        <TextSection icon={Activity} title="Market Trends" text={data.market_trends} />
        <TextSection icon={Shield} title="Regulatory Environment" text={data.regulatory_environment} />
      </div>

      <QuestionsSection questions={data.questions} />
      <ConfidenceIndicator confidence={data.confidence_level} />
      <WebSourcesSection chunks={data.grounding_chunks} />
    </>
  )
}

// ─── PRODUCT TAB ────────────────────────────────────────────────────────────────
function ProductTabContent({ data }: { data: any }) {
  const metrics = data.traction_metrics || {}
  return (
    <>
      <ExecutiveBriefing summary={data.executive_summary} />

      {/* Traction Metrics */}
      {Object.keys(metrics).length > 0 && (
        <div className="mb-10">
          <SectionHeader icon={LineChart} title="Traction Metrics" />
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
            {metrics.total_users != null && <MetricCard label="Total Users" value={formatNumber(metrics.total_users)} />}
            {metrics.monthly_active_users != null && <MetricCard label="MAU" value={formatNumber(metrics.monthly_active_users)} />}
            {metrics.monthly_recurring_revenue != null && <MetricCard label="MRR" value={formatUSD(metrics.monthly_recurring_revenue)} />}
            {metrics.annual_recurring_revenue != null && <MetricCard label="ARR" value={formatUSD(metrics.annual_recurring_revenue)} />}
            {metrics.user_growth_rate != null && <MetricCard label="User Growth" value={`${metrics.user_growth_rate}%`} subtitleColor="text-green-500" />}
            {metrics.revenue_growth_rate != null && <MetricCard label="Rev. Growth" value={`${metrics.revenue_growth_rate}%`} subtitleColor="text-green-500" />}
            {metrics.customer_acquisition_cost != null && <MetricCard label="CAC" value={formatUSD(metrics.customer_acquisition_cost)} />}
            {metrics.lifetime_value != null && <MetricCard label="LTV" value={formatUSD(metrics.lifetime_value)} />}
            {metrics.ltv_cac_ratio != null && <MetricCard label="LTV:CAC" value={`${metrics.ltv_cac_ratio.toFixed(1)}:1`} />}
            {metrics.customer_retention_rate != null && <MetricCard label="Retention" value={`${metrics.customer_retention_rate}%`} />}
            {metrics.daily_active_users != null && <MetricCard label="DAU" value={formatNumber(metrics.daily_active_users)} />}
            {metrics.session_duration != null && <MetricCard label="Avg Session" value={`${metrics.session_duration} min`} />}
            {metrics.feature_adoption_rate != null && <MetricCard label="Feature Adoption" value={`${metrics.feature_adoption_rate}%`} />}
            {metrics.payback_period != null && <MetricCard label="CAC Payback" value={`${metrics.payback_period} mo`} />}
          </div>
          <div className="flex items-center gap-4 mt-3">
            {metrics.metrics_quality && <p className="text-xs text-gray-500">Metrics Quality: {metrics.metrics_quality}</p>}
            {metrics.data_reliability != null && <p className="text-xs text-gray-500">Data Reliability: {(metrics.data_reliability * 100).toFixed(0)}%</p>}
          </div>
        </div>
      )}

      {/* Strengths / Weaknesses / Growth Drivers */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-10">
        <ColoredListCard title="Strengths" items={data.strengths} variant="green" />
        <ColoredListCard title="Weaknesses" items={data.weaknesses} variant="amber" />
        <ColoredListCard title="Growth Drivers" items={data.growth_drivers} variant="blue" />
      </div>

      {/* Scaling Challenges */}
      {data.scaling_challenges?.length > 0 && (
        <div className="mb-10">
          <ColoredListCard title="Scaling Challenges" items={data.scaling_challenges} variant="red" />
        </div>
      )}

      {/* Product Assessment */}
      <div className="grid grid-cols-1 gap-4 mb-10">
        <TextSection icon={Package} title="Product Overview" text={data.product_overview} />
        <TextSection icon={Target} title="Product-Market Fit" text={data.product_market_fit} />
      </div>

      <div className="grid grid-cols-1 gap-4 mb-10">
        <TextSection icon={MessageSquare} title="Customer Feedback" text={data.customer_feedback} />
        <TextSection icon={CheckCircle} title="Use Case Validation" text={data.use_case_validation} />
      </div>

      <div className="grid grid-cols-1 gap-4 mb-10">
        <TextSection icon={Rocket} title="Revenue Model" text={data.revenue_model} />
        <TextSection icon={TrendingUp} title="Scalability Assessment" text={data.scalability_assessment} />
      </div>

      <TextSection icon={LineChart} title="Traction Analysis" text={data.traction_analysis} />
      <QuestionsSection questions={data.questions} />
      <ConfidenceIndicator confidence={data.confidence_level} />
      <WebSourcesSection chunks={data.grounding_chunks} />
    </>
  )
}

// ─── COMPETITION TAB ────────────────────────────────────────────────────────────
function CompetitionTabContent({ data }: { data: any }) {
  const [competitorTab, setCompetitorTab] = useState<'direct' | 'indirect' | 'substitute'>('direct')
  const compScrollRef = useRef<HTMLDivElement>(null)
  const scrollComp = (dir: 'left' | 'right') => {
    if (!compScrollRef.current) return
    compScrollRef.current.scrollBy({ left: dir === 'left' ? -340 : 340, behavior: 'smooth' })
  }
  const competitorSets = {
    direct: { label: 'Direct', items: data.direct_competitors || [] },
    indirect: { label: 'Indirect', items: data.indirect_competitors || [] },
    substitute: { label: 'Substitutes', items: data.substitute_threats || [] },
  }
  const activeCompetitors = competitorSets[competitorTab].items

  return (
    <>
      <ExecutiveBriefing summary={data.executive_summary} />

      {/* Competitors with tab navigation + horizontal scroll */}
      <div className="mb-10">
        <div className="flex items-center justify-between mb-4">
          <SectionHeader icon={Building2} title="Competitors" />
          <div className="flex items-center gap-3">
            <div className="flex gap-1">
              {Object.entries(competitorSets).map(([key, { label, items }]) => (
                items.length > 0 && (
                  <button
                    key={key}
                    onClick={() => setCompetitorTab(key as 'direct' | 'indirect' | 'substitute')}
                    className={`px-4 py-2 text-sm font-bold rounded-xl transition-all ${competitorTab === key
                      ? 'bg-gray-900 dark:bg-white text-white dark:text-gray-900 shadow-md'
                      : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700'
                      }`}
                  >
                    {label} ({items.length})
                  </button>
                )
              ))}
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => scrollComp('left')}
                className="p-1.5 rounded-full border border-gray-200 dark:border-gray-700 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
              >
                <ChevronLeft className="w-4 h-4 text-gray-500" />
              </button>
              <button
                onClick={() => scrollComp('right')}
                className="p-1.5 rounded-full border border-gray-200 dark:border-gray-700 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
              >
                <ChevronRight className="w-4 h-4 text-gray-500" />
              </button>
            </div>
          </div>
        </div>
        <div
          ref={compScrollRef}
          className="flex gap-4 overflow-x-auto scrollbar-hide pb-2 -mx-2 px-2 snap-x snap-mandatory"
        >
          {activeCompetitors.map((c: any, i: number) => (
            <div key={i} className="min-w-[320px] max-w-[360px] flex-shrink-0 snap-start">
              <CompetitorCardComponent competitor={c} />
            </div>
          ))}
        </div>
      </div>

      {/* Competitive Advantages / Moat */}
      {data.competitive_advantages?.length > 0 && (
        <div className="mb-10">
          <SectionHeader icon={Shield} title="Competitive Advantages (Moat)" />
          <div className="grid grid-cols-1 gap-4">
            {data.competitive_advantages.map((adv: any, i: number) => (
              <div key={i} className="bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-700 rounded-2xl p-5 shadow-sm">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="font-bold text-gray-900 dark:text-white">{adv.advantage_type}</h4>
                  {adv.defensibility !== undefined && (
                    <div className="flex items-center gap-1.5">
                      <span className="text-[10px] text-gray-400 uppercase">Score</span>
                      <span className={`text-lg font-bold ${getScoreColor(adv.defensibility)}`}>{adv.defensibility}</span>
                      <span className="text-[10px] text-gray-400">/10</span>
                    </div>
                  )}
                </div>
                {adv.description && <p className="text-sm text-gray-600 dark:text-gray-300 mb-2"><TextWithCitations>{adv.description}</TextWithCitations></p>}
                {adv.sustainability && (
                  <div className="text-xs text-gray-500 dark:text-gray-400 mt-2 pt-2 border-t border-gray-100 dark:border-gray-700">
                    <span className="font-bold uppercase text-[10px]">Sustainability: </span>
                    <TextWithCitations>{adv.sustainability}</TextWithCitations>
                  </div>
                )}
                {adv.evidence?.length > 0 && (
                  <div className="mt-2 pt-2 border-t border-gray-100 dark:border-gray-700">
                    <span className="font-bold uppercase text-[10px] text-gray-400">Evidence</span>
                    <ul className="mt-1 space-y-1">
                      {adv.evidence.map((e: string, j: number) => (
                        <li key={j} className="text-xs text-gray-500 dark:text-gray-400 flex items-start gap-1.5">
                          <span className="w-1 h-1 rounded-full bg-blue-400 mt-1.5 flex-shrink-0" />
                          <TextWithCitations>{e}</TextWithCitations>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Key Differentiators */}
      {data.key_differentiators?.length > 0 && (
        <div className="mb-10">
          <SectionHeader icon={Zap} title="Key Differentiators" />
          <div className="grid grid-cols-1 gap-3">
            {data.key_differentiators.map((diff: string, i: number) => (
              <div key={i} className="flex items-start gap-3 bg-blue-50 dark:bg-blue-900/10 border border-blue-100 dark:border-blue-800 rounded-xl p-4">
                <span className="w-7 h-7 rounded-full bg-blue-500 text-white flex items-center justify-center text-xs font-bold flex-shrink-0">{i + 1}</span>
                <p className="text-sm text-gray-700 dark:text-gray-300"><TextWithCitations>{diff}</TextWithCitations></p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Strategy & Positioning */}
      <div className="grid grid-cols-1 gap-4 mb-10">
        <TextSection icon={Target} title="Competitive Landscape" text={data.competitive_landscape} />
        <TextSection icon={Compass} title="Market Positioning" text={data.market_positioning} />
      </div>

      <div className="grid grid-cols-1 gap-4 mb-10">
        <TextSection icon={Lock} title="Moat Analysis" text={data.moat_analysis} />
        <TextSection icon={TrendingDown} title="Barriers to Entry" text={data.barriers_to_entry} />
      </div>

      <div className="grid grid-cols-1 gap-4 mb-10">
        <TextSection icon={AlertTriangle} title="Competitive Threats" text={data.competitive_threats} />
        <TextSection icon={Rocket} title="Competitive Strategy" text={data.competitive_strategy} />
      </div>

      {data.switching_costs && <TextSection icon={DollarSign} title="Switching Costs" text={data.switching_costs} />}
      {data.differentiation_analysis && <TextSection icon={Eye} title="Differentiation Analysis" text={data.differentiation_analysis} />}
      {data.competitive_risks && <TextSection icon={AlertTriangle} title="Competitive Risks" text={data.competitive_risks} />}

      <RecommendationsSection recommendations={data.positioning_recommendations} />
      <QuestionsSection questions={data.questions} />
      <ConfidenceIndicator confidence={data.confidence_level} />
      <WebSourcesSection chunks={data.grounding_chunks} />
    </>
  )
}

// Risk/Opportunity level badge component
function LevelBadge({ level, type }: { level: string; type: 'risk' | 'opportunity' }) {
  const riskColors: Record<string, string> = {
    Low: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
    Medium: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
    High: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
    Critical: 'bg-red-200 text-red-800 dark:bg-red-900/50 dark:text-red-300',
  }
  const oppColors: Record<string, string> = {
    Low: 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-400',
    Medium: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
    High: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
    Exceptional: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400',
  }
  const colors = type === 'risk' ? riskColors : oppColors
  return (
    <span className={`px-3 py-1 rounded-full text-xs font-bold uppercase ${colors[level] || 'bg-gray-100 text-gray-600'}`}>
      {level}
    </span>
  )
}

// Quality metrics bar (for synthesis)
function QualityMetricsBar({ completeness, dataQuality, confidence }: {
  completeness?: number | null; dataQuality?: number | null; confidence?: number | null
}) {
  const metrics = [
    { label: 'Analysis Completeness', value: completeness },
    { label: 'Data Quality', value: dataQuality },
    { label: 'Confidence', value: confidence },
  ].filter(m => m.value != null)
  if (metrics.length === 0) return null
  return (
    <div className="mb-10">
      <SectionHeader icon={BarChart3} title="Quality Metrics" />
      <div className="bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-700 rounded-2xl p-6 shadow-sm">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {metrics.map(({ label, value }) => {
            const pct = Math.round((value ?? 0) * 100)
            const color = pct >= 70 ? 'bg-green-500' : pct >= 40 ? 'bg-amber-500' : 'bg-red-500'
            return (
              <div key={label}>
                <div className="flex items-center justify-between mb-1.5">
                  <span className="text-xs font-bold text-gray-500 uppercase tracking-wider">{label}</span>
                  <span className="text-sm font-bold text-gray-900 dark:text-white">{pct}%</span>
                </div>
                <div className="h-2 bg-gray-100 dark:bg-gray-700 rounded-full overflow-hidden">
                  <div className={`h-full ${color} rounded-full transition-all duration-500`} style={{ width: `${pct}%` }} />
                </div>
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}

// ─── SYNTHESIS TAB ──────────────────────────────────────────────────────────────
function SynthesisTabContent({ data }: { data: any }) {
  const rec = data.investment_recommendation || {}
  const risk = data.risk_assessment || {}
  const opp = data.opportunity_assessment || {}
  const questions = data.questions || []

  // Group founder questions by category
  const questionsByCategory: Record<string, any[]> = {}
  questions.forEach((q: any) => {
    const cat = (typeof q === 'object' ? q.category : 'general') || 'general'
    if (!questionsByCategory[cat]) questionsByCategory[cat] = []
    questionsByCategory[cat].push(q)
  })

  const priorityColors: Record<string, string> = {
    high: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
    medium: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
    low: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
  }

  const categoryLabels: Record<string, string> = {
    team: 'Team', market: 'Market', product: 'Product',
    financials: 'Financials', business_model: 'Business Model', general: 'General',
  }

  return (
    <>
      <ExecutiveBriefing summary={data.executive_summary} />

      {/* Investment Thesis */}
      {data.investment_thesis && (
        <div className="mb-10">
          <TextSection icon={FileText} title="Investment Thesis" text={data.investment_thesis} />
        </div>
      )}

      {/* Investment Recommendation */}
      {Object.keys(rec).length > 0 && (
        <div className="mb-10">
          <SectionHeader icon={CheckSquare} title="Investment Recommendation" />
          <div className="bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-700 rounded-2xl p-6 shadow-sm">
            {/* Header: recommendation badge + confidence */}
            <div className="flex items-center gap-4 mb-5">
              {rec.recommendation && (
                <Badge className={`${getInvestmentRecommendationColor(rec.recommendation)} text-sm px-4 py-1.5`}>
                  {rec.recommendation}
                </Badge>
              )}
              {rec.confidence_level != null && (
                <span className="text-sm text-gray-500">Confidence: <span className="font-bold text-gray-900 dark:text-white">{Math.round(rec.confidence_level * 100)}%</span></span>
              )}
            </div>

            {/* Rationale */}
            {rec.rationale && (
              <div className="mb-5 p-4 bg-gray-50 dark:bg-gray-900/50 rounded-xl border border-gray-100 dark:border-gray-700">
                <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">Rationale</span>
                <div className="mt-2 text-sm text-gray-600 dark:text-gray-300 leading-relaxed">
                  <TextWithCitations>{rec.rationale}</TextWithCitations>
                </div>
              </div>
            )}

            {/* Valuation & Investment Size metrics */}
            {(rec.suggested_valuation_range || rec.investment_size_recommendation) && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-5">
                {rec.suggested_valuation_range && <MetricCard label="Suggested Valuation" value={rec.suggested_valuation_range} />}
                {rec.investment_size_recommendation && <MetricCard label="Recommended Investment" value={rec.investment_size_recommendation} />}
              </div>
            )}

            {/* Key Terms / Investment Conditions / Due Diligence */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <ColoredListCard title="Key Terms" items={rec.key_terms} variant="blue" />
              <ColoredListCard title="Investment Conditions" items={rec.investment_conditions} variant="amber" />
              <ColoredListCard title="Due Diligence Required" items={rec.due_diligence_requirements} variant="red" />
            </div>
          </div>
        </div>
      )}

      {/* Risk Assessment */}
      {Object.keys(risk).length > 0 && (
        <div className="mb-10">
          <div className="flex items-center gap-3 mb-4">
            <AlertTriangle className="w-5 h-5 text-red-500" />
            <h3 className="text-lg font-bold text-gray-900 dark:text-white">Risk Assessment</h3>
            {risk.overall_risk_level && <LevelBadge level={risk.overall_risk_level} type="risk" />}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
            <ColoredListCard title="Team Risks" items={risk.team_risks} variant="red" />
            <ColoredListCard title="Market Risks" items={risk.market_risks} variant="red" />
            <ColoredListCard title="Product Risks" items={risk.product_risks} variant="red" />
            <ColoredListCard title="Competitive Risks" items={risk.competitive_risks} variant="amber" />
            <ColoredListCard title="Execution Risks" items={risk.execution_risks} variant="amber" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <ColoredListCard title="Mitigation Strategies" items={risk.mitigation_strategies} variant="green" />
            <ColoredListCard title="Monitoring Requirements" items={risk.monitoring_requirements} variant="blue" />
          </div>
        </div>
      )}

      {/* Opportunity Assessment */}
      {Object.keys(opp).length > 0 && (
        <div className="mb-10">
          <div className="flex items-center gap-3 mb-4">
            <TrendingUp className="w-5 h-5 text-green-500" />
            <h3 className="text-lg font-bold text-gray-900 dark:text-white">Opportunity Assessment</h3>
            {opp.overall_opportunity_level && <LevelBadge level={opp.overall_opportunity_level} type="opportunity" />}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <ColoredListCard title="Market Opportunities" items={opp.market_opportunities} variant="green" />
            <ColoredListCard title="Product Opportunities" items={opp.product_opportunities} variant="green" />
            <ColoredListCard title="Expansion Opportunities" items={opp.expansion_opportunities} variant="blue" />
            <ColoredListCard title="Strategic Opportunities" items={opp.strategic_opportunities} variant="blue" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <ColoredListCard title="Value Creation Drivers" items={opp.value_creation_drivers} variant="green" />
            <ColoredListCard title="Exit Scenarios" items={opp.exit_scenarios} variant="amber" />
          </div>
        </div>
      )}

      {/* Comparable Companies */}
      {data.comparable_companies?.length > 0 && (
        <div className="mb-10">
          <SectionHeader icon={Building2} title="Comparable Companies" />
          <div className="flex flex-wrap gap-2">
            {data.comparable_companies.map((company: string, i: number) => (
              <span key={i} className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 text-sm font-medium rounded-full border border-gray-200 dark:border-gray-700">
                <Building2 className="w-3.5 h-3.5 text-gray-400" />
                {company}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Competitive Positioning */}
      {data.competitive_positioning && (
        <div className="mb-10">
          <TextSection icon={Target} title="Competitive Positioning" text={data.competitive_positioning} />
        </div>
      )}

      {/* Next Steps */}
      {data.next_steps?.length > 0 && (
        <div className="mb-10">
          <SectionHeader icon={Rocket} title="Next Steps" />
          <div className="bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-700 rounded-2xl p-6 shadow-sm">
            <ul className="space-y-3">
              {data.next_steps.map((step: string, i: number) => (
                <li key={i} className="flex items-start gap-3 text-sm text-gray-600 dark:text-gray-300">
                  <span className="flex-shrink-0 w-6 h-6 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center text-xs font-bold text-blue-600">{i + 1}</span>
                  <TextWithCitations>{step}</TextWithCitations>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}

      {/* Timeline */}
      {data.timeline && (
        <div className="mb-10">
          <TextSection icon={CalendarDays} title="Decision Timeline" text={data.timeline} />
        </div>
      )}

      {/* Quality Metrics */}
      <QualityMetricsBar
        completeness={data.analysis_completeness}
        dataQuality={data.data_quality}
        confidence={data.confidence_level}
      />

      {/* Founder Questions — grouped by category with priority badges */}
      {questions.length > 0 && (
        <div className="mb-10">
          <SectionHeader icon={MessageSquare} title="Questions for Founders" />
          <div className="space-y-6">
            {Object.entries(questionsByCategory).map(([category, qs]) => (
              <div key={category}>
                <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-3 flex items-center gap-2">
                  <span className="px-2 py-0.5 bg-gray-100 dark:bg-gray-800 rounded">{categoryLabels[category] || category}</span>
                  <span className="text-gray-300 dark:text-gray-600">({qs.length})</span>
                </h4>
                <div className="bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-700 rounded-2xl p-5 shadow-sm">
                  <ul className="space-y-3">
                    {qs.map((q: any, i: number) => {
                      const text = typeof q === 'string' ? q : q.question_text
                      const priority = typeof q === 'object' ? q.priority : null
                      return (
                        <li key={i} className="flex items-start gap-3 text-sm text-gray-600 dark:text-gray-300">
                          <span className="flex-shrink-0 w-6 h-6 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center text-xs font-bold text-blue-600">{i + 1}</span>
                          <div className="flex-1">
                            <TextWithCitations>{text}</TextWithCitations>
                          </div>
                          {priority && (
                            <span className={`flex-shrink-0 px-2 py-0.5 rounded text-[10px] font-bold uppercase ${priorityColors[priority] || 'bg-gray-100 text-gray-600'}`}>
                              {priority}
                            </span>
                          )}
                        </li>
                      )
                    })}
                  </ul>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </>
  )
}


export function AnalysisPage() {
  const { id } = useParams<{ id: string }>()
  const { toast } = useToast()
  const queryClient = useQueryClient()
  const [isReanalysisModalOpen, setIsReanalysisModalOpen] = useState(false)
  const [investorNotes, setInvestorNotes] = useState('')
  const [isPageFeedbackOpen, setIsPageFeedbackOpen] = useState(false)

  // Document preview state
  const [previewOpen, setPreviewOpen] = useState(false)
  const [previewGcsPath, setPreviewGcsPath] = useState('')
  const [previewFilename, setPreviewFilename] = useState('')
  const [previewPage, setPreviewPage] = useState<number | null>(null)

  // Listen for document preview events from citation links
  useEffect(() => {
    const handler = (e: Event) => {
      const { gcsPath, filename, page } = (e as CustomEvent).detail
      setPreviewGcsPath(gcsPath)
      setPreviewFilename(filename)
      setPreviewPage(page)
      setPreviewOpen(true)
    }
    window.addEventListener(DOCUMENT_PREVIEW_EVENT, handler)
    return () => window.removeEventListener(DOCUMENT_PREVIEW_EVENT, handler)
  }, [])

  const { data: analyses, isLoading } = useQuery({
    queryKey: ['analyses', id],
    queryFn: () => analysisApi.list({ startup_id: id }),
    enabled: !!id,
  })

  // Get startup data for founder email
  const { data: startup } = useQuery({
    queryKey: ['startup', id],
    queryFn: () => startupsApi.get(id!),
    enabled: !!id,
  })

  // Get startup status
  const { data: startupStatus } = useQuery({
    queryKey: ['startupStatus', id],
    queryFn: () => startupStatusApi.getStatus(id!),
    enabled: !!id,
  })

  const latestAnalysis = analyses?.data?.[0]

  // Status update mutation
  const updateStatusMutation = useMutation({
    mutationFn: ({ status }: { status: string }) => startupStatusApi.updateStatus(id!, status),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['startupStatus', id] })
      toast({
        title: 'Status Updated',
        description: 'Startup status has been updated successfully',
      })
    },
    onError: () => {
      toast({
        title: 'Error',
        description: 'Failed to update status',
        variant: 'destructive',
      })
    },
  })

  const handleStatusUpdate = (status: string) => {
    updateStatusMutation.mutate({ status })
  }

  // Re-analysis mutation
  const reanalysisMutation = useMutation({
    mutationFn: ({ notes }: { notes: string }) => analysisApi.triggerReanalysis(id!, notes),
    onSuccess: (data) => {
      setIsReanalysisModalOpen(false)
      setInvestorNotes('')
      queryClient.invalidateQueries({ queryKey: ['analyses', id] })
      toast({
        title: 'Re-analysis Started',
        description: `Analysis ${data.analysis_id} has been queued. The AI agents will re-analyze the startup with your notes.`,
      })
    },
    onError: (error: any) => {
      if (error.response?.status === 402) {
        toast({
          title: 'Analysis Limit Reached',
          description: 'You have reached your monthly analysis limit. Please upgrade your plan to continue.',
          variant: 'destructive',
        })
        return
      }
      toast({
        title: 'Error',
        description: error.message || 'Failed to trigger re-analysis',
        variant: 'destructive',
      })
    },
  })

  const handleReanalysis = () => {
    if (!investorNotes.trim()) {
      toast({
        title: 'Notes Required',
        description: 'Please provide investor notes for the re-analysis',
        variant: 'destructive',
      })
      return
    }
    reanalysisMutation.mutate({ notes: investorNotes })
  }

  const handleScheduleMeeting = () => {
    const founderEmail = startup?.profile?.company_info?.founders?.[0]?.email
    if (founderEmail) {
      window.location.href = `mailto:${founderEmail}?subject=Meeting Request - ${startup?.profile?.company_info?.name}`
    }
  }

  const handleShare = () => {
    const url = window.location.href
    navigator.clipboard.writeText(url)
    toast({
      title: 'Link Copied!',
      description: 'Analysis page link copied to clipboard',
    })
  }

  const currentStatus = startupStatus?.status || 'New'

  // Parse individual agent analysis columns from BigQuery schema
  const parseAnalysis = (data: any) => {
    if (!data) return null
    if (typeof data === 'string') {
      try {
        return JSON.parse(data)
      } catch {
        return null
      }
    }
    return data
  }

  const teamAnalysis = parseAnalysis(latestAnalysis?.team_analysis)
  const marketAnalysis = parseAnalysis(latestAnalysis?.market_analysis)
  const productAnalysis = parseAnalysis(latestAnalysis?.product_analysis)
  const competitionAnalysis = parseAnalysis(latestAnalysis?.competition_analysis)
  const synthesisAnalysis = parseAnalysis(latestAnalysis?.synthesis_analysis)

  // Parse tags from analysis
  const analysisTags = (() => {
    let raw = latestAnalysis?.tags
    if (!raw) return []
    if (typeof raw === 'string') {
      try { raw = JSON.parse(raw) } catch { return [] }
    }
    if (!Array.isArray(raw)) return []
    // Sort: green first, then red; within each group sort by category
    return [...raw]
      .filter((t: any) => typeof t.similarity === 'number' ? t.similarity > 0.40 : true)
      .sort((a: any, b: any) => {
        if (a.type !== b.type) return a.type === 'green' ? -1 : 1
        return (a.category || '').localeCompare(b.category || '')
      })
  })()

  // Note: Now using individual analysis columns from BigQuery schema

  // Parse investment recommendation if it's a JSON string
  const parseInvestmentRecommendation = (recommendation: any) => {
    if (!recommendation) return 'PENDING'
    if (typeof recommendation === 'string') {
      try {
        const parsed = JSON.parse(recommendation)
        return parsed.recommendation || parsed.investment_recommendation || recommendation
      } catch {
        return recommendation
      }
    }
    return recommendation.recommendation || recommendation.investment_recommendation || 'PENDING'
  }

  // Create investability score object for the UI using BigQuery fields
  const investabilityScore = latestAnalysis?.investability_score || {
    overall_score: latestAnalysis?.overall_score || 0,
    team_score: latestAnalysis?.team_score || 0,
    market_score: latestAnalysis?.market_score || 0,
    product_score: latestAnalysis?.product_score || 0,
    competition_score: latestAnalysis?.competition_score || 0,
    investment_recommendation: parseInvestmentRecommendation(latestAnalysis?.investment_recommendation),
    confidence_level: latestAnalysis?.confidence_level || 0.5,
    score_weights: {}
  }

  if (isLoading) {
    return (
      <div className="p-6">
        <div className="animate-pulse space-y-6">
          <div className="h-8 bg-muted rounded w-1/4"></div>
          <div className="grid gap-4 md:grid-cols-4">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="h-32 bg-muted rounded"></div>
            ))}
          </div>
          <div className="h-96 bg-muted rounded"></div>
        </div>
      </div>
    )
  }

  if (!latestAnalysis) {
    return (
      <div className="p-6">
        <div className="text-center py-12">
          <BarChart3 className="w-16 h-16 mx-auto mb-4 text-muted-foreground" />
          <h3 className="text-lg font-semibold mb-2">No analysis available</h3>
          <p className="text-muted-foreground mb-4">
            This startup hasn't been analyzed yet or you don't have access to the analysis.
          </p>
          <Button asChild>
            <Link to={`/startups/${id}`}>
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Startup
            </Link>
          </Button>
        </div>
      </div>
    )
  }

  return (
    <BeaconProvider>
      <div className="min-h-screen bg-white dark:bg-background-dark text-slate-950 dark:text-white antialiased transition-colors duration-300" style={displayFont}>
        <div className="p-4 md:p-6 space-y-6">
          {/* Modern Gradient Header Card - Responsive */}
          <StartupAnalysisBanner onReanalyzeClick={() => setIsReanalysisModalOpen(true)} />


          {/* Tags Display */}
          {analysisTags.length > 0 && (
            <div className="flex flex-wrap gap-1.5 justify-center px-4">
              {analysisTags.map((tag: any, i: number) => {
                const categoryIcons: Record<string, any> = {
                  'Team & Founders': Users,
                  'Market & Economics': TrendingUp,
                  'AI & Technology': Zap,
                  'Product & Traction': Package,
                  'Strategy & Philosophy': Compass,
                }
                const TagIcon = categoryIcons[tag.category] || Star
                const isGreen = tag.type === 'green'
                const isThesis = tag.is_user_thesis
                return (
                  <div
                    key={i}
                    className={`group relative inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-semibold cursor-default transition-all hover:shadow-md ${isGreen
                      ? 'bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400 border border-green-200 dark:border-green-800'
                      : 'bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400 border border-red-200 dark:border-red-800'
                      } ${isThesis ? 'ring-2 ring-yellow-400 ring-offset-1 dark:ring-offset-gray-900' : ''}`}
                  >
                    <TagIcon className="w-3 h-3 flex-shrink-0" />
                    <span className="whitespace-nowrap">{tag.name}</span>
                    {isThesis && <span className="text-yellow-500 text-[9px]">★</span>}
                    {/* Hover tooltip */}
                    <div className="opacity-0 invisible group-hover:opacity-100 group-hover:visible absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-64 p-3 bg-gray-900 dark:bg-gray-700 text-white text-xs leading-relaxed rounded-lg shadow-lg z-50 pointer-events-none transition-all duration-200">
                      {tag.definition}{isThesis ? ' ⭐ Your Thesis' : ''}
                      <div className="absolute top-full left-1/2 -translate-x-1/2 w-0 h-0 border-x-[6px] border-x-transparent border-t-[6px] border-t-gray-900 dark:border-t-gray-700" />
                    </div>
                  </div>
                )
              })}
            </div>
          )}

          <Tabs defaultValue="synthesis" className="space-y-4 md:space-y-8">
            {/* Pill-style Tab Bar with Inline Scores — Doubled Size */}
            <div className="sticky top-[80px] flex justify-center -mt-3 z-30 pointer-events-none">
              <div className="inline-flex items-center bg-white/90 dark:bg-gray-900/90 backdrop-blur-md rounded-full shadow-xl border border-gray-200 dark:border-gray-700 p-2 gap-1 pointer-events-auto">
                <TabsList className="flex bg-transparent p-0 h-auto gap-1">
                  {[
                    { value: 'team', icon: Users, label: 'Team', score: investabilityScore?.team_score },
                    { value: 'market', icon: TrendingUp, label: 'Market', score: investabilityScore?.market_score },
                    { value: 'product', icon: Package, label: 'Product', score: investabilityScore?.product_score },
                    { value: 'competition', icon: Target, label: 'Competition', score: investabilityScore?.competition_score },
                    { value: 'synthesis', icon: CheckCircle, label: 'Verdict', score: investabilityScore?.overall_score },
                  ].map((tab) => (
                    <TabsTrigger
                      key={tab.value}
                      value={tab.value}
                      className="data-[state=active]:bg-logo-gradient data-[state=active]:text-white data-[state=active]:shadow-lg font-bold text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 transition-all rounded-full py-3 px-5 md:px-6 text-sm md:text-base border-none"
                    >
                      <div className="flex items-center gap-2 md:gap-2.5">
                        <tab.icon className="w-4 h-4 md:w-5 md:h-5" />
                        <span className="hidden sm:inline">{tab.label}</span>
                        {tab.score !== undefined && tab.score !== null && (
                          <span className="font-black text-sm md:text-base">{tab.score.toFixed(1)}</span>
                        )}
                      </div>
                    </TabsTrigger>
                  ))}
                </TabsList>
              </div>
            </div>

            <TabsContent value="team" className="space-y-6">
              <AnalysisRenderer
                data={teamAnalysis}
                title="Team Analysis"
                icon={Users}
                analysisType="team"
              />
            </TabsContent>

            <TabsContent value="market" className="space-y-6">
              <AnalysisRenderer
                data={marketAnalysis}
                title="Market Analysis"
                icon={TrendingUp}
                analysisType="market"
              />
            </TabsContent>

            <TabsContent value="product" className="space-y-6">
              <AnalysisRenderer
                data={productAnalysis}
                title="Product Analysis"
                icon={Target}
                analysisType="product"
              />
            </TabsContent>

            <TabsContent value="competition" className="space-y-6">
              <AnalysisRenderer
                data={competitionAnalysis}
                title="Competition Analysis"
                icon={Shield}
                analysisType="competition"
              />
            </TabsContent>

            <TabsContent value="synthesis" className="space-y-6">
              {/* Investment Summary Card - At the top */}
              {investabilityScore && (
                <Card className="glass-card shadow-xl border-white/50 dark:border-slate-700/50">
                  <CardHeader className="relative overflow-hidden p-6 rounded-t-2xl">
                    <div className="absolute inset-0 bg-logo-gradient opacity-10" />
                    <CardTitle className="flex items-center gap-2 text-slate-800 dark:text-slate-200 relative z-10" style={headingFont}>
                      <CheckCircle className="w-5 h-5 text-[#4194F2]" />
                      Investment Summary
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="pt-6">
                    <div className="flex items-center gap-4 mb-4">
                      <Badge
                        className={`${getInvestmentRecommendationColor(investabilityScore.investment_recommendation)} text-lg px-4 py-2`}
                      >
                        {investabilityScore.investment_recommendation}
                      </Badge>
                      <div className="text-sm text-muted-foreground">
                        Overall Score: <span className="font-medium">{investabilityScore.overall_score.toFixed(1)}/10</span>
                      </div>
                      <div className="text-sm text-muted-foreground">
                        Confidence: {(investabilityScore.confidence_level * 100).toFixed(0)}%
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Investment Memo Section - Right after Summary */}
              {latestAnalysis?.investment_memo && (
                <InvestmentMemoSection
                  memo={latestAnalysis.investment_memo}
                  companyName={latestAnalysis.company_name && latestAnalysis.company_name !== 'Startup' ? latestAnalysis.company_name : (startup?.profile?.company_info?.name || 'Startup')}
                  analysisDate={latestAnalysis.completed_at ? new Date(latestAnalysis.completed_at).toLocaleDateString() : undefined}
                  recommendation={investabilityScore?.investment_recommendation}
                  scores={{
                    team: investabilityScore?.team_score,
                    market: investabilityScore?.market_score,
                    product: investabilityScore?.product_score,
                    competition: investabilityScore?.competition_score,
                    overall: investabilityScore?.overall_score,
                  }}
                />
              )}


              {/* Full Analysis Renderer - Contains remaining sections */}
              <AnalysisRenderer
                data={synthesisAnalysis}
                title="Final Verdict"
                icon={BarChart3}
                analysisType="synthesis"
              />
            </TabsContent>

          </Tabs>

          {/* AI Disclaimer */}
          <div className="mt-12 mb-4 px-4 max-w-5xl mx-auto">
            <div className="flex items-center justify-center gap-2 text-slate-500 dark:text-slate-400 text-[11px] text-center">
              <AlertTriangle className="w-3.5 h-3.5 text-amber-500 flex-shrink-0" />
              <span>
                This is an AI generated Analysis and is prone to mistakes. The aim is to assist you in your diligence process.{' '}
                <button
                  type="button"
                  onClick={() => setIsPageFeedbackOpen(true)}
                  className="text-indigo-600 dark:text-indigo-400 hover:text-indigo-700 dark:hover:text-indigo-300 hover:underline font-medium focus:outline-none"
                >
                  Please share feedback
                </button>
              </span>
            </div>
          </div>

          {/* Re-analysis Modal */}
          <Dialog open={isReanalysisModalOpen} onOpenChange={setIsReanalysisModalOpen}>
            <DialogContent className="sm:max-w-[600px]">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <RefreshCw className="w-5 h-5 text-purple-600" />
                  Request Re-analysis
                </DialogTitle>
                <DialogDescription>
                  Provide specific notes or focus areas for the AI agents to consider during re-analysis.
                  The agents will incorporate answered questions, new attachments, and your guidance to generate an updated analysis.
                </DialogDescription>
              </DialogHeader>

              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label htmlFor="investor-notes" className="text-base font-medium">
                    Investor Notes <span className="text-red-500">*</span>
                  </Label>
                  <Textarea
                    id="investor-notes"
                    placeholder="Example: Focus on financial viability and team strength. Pay special attention to the updated financial projections and new CTO hire. Assess scalability concerns and competitive positioning in the European market."
                    value={investorNotes}
                    onChange={(e) => setInvestorNotes(e.target.value)}
                    rows={8}
                    className="resize-none"
                  />
                  <p className="text-sm text-muted-foreground">
                    Be specific about what aspects you want the AI to focus on (e.g., financials, team, market, product, competition).
                  </p>
                </div>

                {/* Info Box */}
                <div className="bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                  <div className="flex gap-3">
                    <Lightbulb className="w-5 h-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
                    <div className="space-y-1">
                      <p className="text-sm font-medium text-blue-900 dark:text-blue-100">
                        What happens during re-analysis?
                      </p>
                      <ul className="text-sm text-blue-800 dark:text-blue-200 space-y-1 list-disc list-inside">
                        <li>AI agents will review all answered questions and their attachments</li>
                        <li>Your notes will guide the focus areas for analysis</li>
                        <li>A new comprehensive analysis will be generated</li>
                        <li>Previous analysis will remain accessible for comparison</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>

              <DialogFooter>
                <Button
                  variant="ghost"
                  onClick={() => {
                    setIsReanalysisModalOpen(false)
                    setInvestorNotes('')
                  }}
                  disabled={reanalysisMutation.isPending}
                  className="font-bold text-slate-500"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleReanalysis}
                  disabled={reanalysisMutation.isPending || !investorNotes.trim()}
                  className="btn-logo-gradient-80 text-white font-bold px-8 rounded-xl shadow-lg"
                >
                  {reanalysisMutation.isPending ? (
                    <>
                      <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                      Starting...
                    </>
                  ) : (
                    <span className="flex items-center">
                      <Zap className="w-4 h-4 mr-2" />
                      Run AI Agents
                    </span>
                  )}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          {/* Beacon Chat Panel */}
          <BeaconChat
            startupId={id!}
            analysisData={latestAnalysis}
            startupData={startup}
          />

          {/* Beacon Floating Button */}
          <BeaconButton />

          {/* Document Preview Modal */}
          <DocumentPreviewModal
            open={previewOpen}
            onClose={() => setPreviewOpen(false)}
            gcsPath={previewGcsPath}
            startupId={id!}
            filename={previewFilename}
            page={previewPage}
          />

          <FeedbackModal
            open={isPageFeedbackOpen}
            onOpenChange={setIsPageFeedbackOpen}
            page="Analysis Page"
            section="Overall Page"
          />
        </div>
      </div >
    </BeaconProvider >
  )
}
